import DatePicker from 'components/base/DatePicker';
import LoginAttemptsChart from 'components/charts/LoginAttemptsChart';
import {
  faPersonWalkingDashedLineArrowRight,
  faShieldDog,
  faPeopleGroup,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import UserKPICard from 'components/cards/UserKPICard';
import UserKPICard2 from 'components/cards/UserKPICard2';
import ActivityTimeline from 'components/timelines/ActivityTimeline';
import AccountList from 'pages/systemmanagement/AccountList';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AccountKPIsService, AccountKPIs } from 'services/accountKPIs.service';

const UserManagementDashboard = () => {
  const { setContentClass } = useMainLayoutContext();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [accountKPIs, setAccountKPIs] = useState<AccountKPIs | null>(null);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');

    return () => {
      setContentClass('');
    };
  }, []);

  useEffect(() => {
    // Llamar al servicio para obtener los KPIs de cuentas
    const fetchKPIs = async () => {
      const data = await AccountKPIsService.getAccountKPIs();
      setAccountKPIs(data);
    };

    fetchKPIs();
  }, []);

  const handleDateChange = (date: Date[]) => {
    if (date && date.length > 0) {
      setSelectedDate(date[0]);
    }
  };

  return (
    <Row className="g-0">
      <Col xs={12} xxl={9} className="px-0">
        <div className="px-4 px-lg-6 pt-5 pb-6 bg-body">
          <div className="mb-5">
            <div className="d-flex justify-content-between">
              <h2 className="text-body-emphasis fw-bolder mb-2">
                HVA System Management Dashboard
              </h2>
            </div>
          </div>
          <Row className="g-3 mb-3">
            <Col xs={12} sm={12} md={12} xl={4} xxl={4}>
              <UserKPICard
                icon={faPeopleGroup}
                color="primary-dark"
                label="Today"
                label2="System Admin accounts: "
                label3="New accounts (last 30 days): "
                label4="Logged accounts (last 30 days): "
                value={accountKPIs?.totalAccounts || 0}
                value2={accountKPIs?.systemAdminAccounts || 0}
                value3={accountKPIs?.recentRegisteredAccounts || 0}
                value4={accountKPIs?.recentSeenAccounts || 0}
                title="Accounts"
              />
            </Col>
            <Col xs={12} sm={12} md={6} xl={4} xxl={4}>
              <UserKPICard2
                icon={faPersonWalkingDashedLineArrowRight}
                color="primary-dark"
                label="Last 30 days"
                title="LogIns"
                value={
                  accountKPIs?.accountLogs.reduce(
                    (total, log) => total + log.count,
                    0
                  ) || 0
                }
                data={accountKPIs?.accountLogs || []}
              />
            </Col>
            <Col xs={12} sm={12} md={6} xl={4} xxl={4}>
              <UserKPICard2
                icon={faShieldDog}
                color="primary-dark"
                label="Last 30 days"
                title="Attempts"
                value={
                  accountKPIs?.loginAttempts.reduce(
                    (total, attempt) => total + attempt.count,
                    0
                  ) || 0
                }
                data={accountKPIs?.loginAttempts || []}
              />
            </Col>
          </Row>
          <Row className="gx-1 gx-sm-5 gy-4 mb-0 px-2 pt-2 justify-content-between">
            <Col md={9} xs="auto">
              <div>
                <h4 className="mb-1">System login attempts</h4>
              </div>
              <div className="text-body-secondary mb-0 mt-0 fs-10 lh-1">
                Please verify that the number of login attempts aligns with the
                current number
                <br />
                of users. If the count is too low, consider removing some
                accounts. Conversely,
                <br />
                if the count is too high, it’s possible that the system is under
                attack.
              </div>
            </Col>
            <Col md={3} xs="auto" className="pt-4">
              <DatePicker
                value={[selectedDate]}
                options={{
                  dateFormat: 'M j, Y',
                  defaultDate: selectedDate
                }}
                onChange={handleDateChange}
              />
            </Col>
          </Row>
          <Row className="gx-1 gx-sm-5 gy-4 mb-0 px-2">
            <div>
              <LoginAttemptsChart selectedDate={selectedDate} />
            </div>
          </Row>
        </div>
        <div className="px-4 px-lg-6 pt-6 pb-0 bg-body-emphasis dark__bg-gray-1100 border-top">
          <Row className="gx-1 gx-sm-5 mb-0">
            <div>
              <h3 className="text-body-emphasis">Current system users</h3>
            </div>
            <div className="text-body-secondary mb-5">
              Users that are not using the system should be deleted.
            </div>
            <div className="mb-0">
              <AccountList />
            </div>
          </Row>
        </div>
      </Col>
      <Col xs={12} xxl={3} className="px-0 border-start-xxl">
        <div className="h-100">
          <div className="bg-light dark__bg-gray-1100 h-100">
            <div className="p-4 p-lg-5 pb-0 pb-lg-0">
              <Row className="d-flex justify-content-between align-items-center">
                <Col xs="auto" xxl="auto" className="pe-0">
                  <h4 className="text-body-highlight fw-bold mb-0">
                    Recent activity
                  </h4>
                </Col>
                <Col className="text-end m-0 p-0 pe-2 lh-1 pt-2">
                  <Link to={`/system-management/system-log`}>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none fs-10"
                    >
                      View All
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="px-1 pe-0"
                      />
                    </Button>
                  </Link>
                </Col>
              </Row>
            </div>
            <div className="p-4 p-lg-5 pt-0 pt-lg-0">
              <div className="fs-9 fw-normal text-body-terciary pt-0 pb-4">
                (last 7 days)
              </div>
              <ActivityTimeline />
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default UserManagementDashboard;
